import React, { useEffect, useState } from 'react';
import { Cloud, Sun, CloudRain, Snowflake, Wind, CloudLightning, Loader2 } from 'lucide-react';
import { useTemplate } from '@/context/TemplateContext';
import { pmsFrom, invokeFunction } from '@/integrations/pms';

interface WeatherData {
  temp: string;
  condition: string;
  icon: string;
}

const getWeatherIcon = (condition: string) => {
  const lowerCondition = condition.toLowerCase();
  if (lowerCondition.includes('sun') || lowerCondition.includes('clear')) {
    return <Sun className="h-5 w-5 text-yellow-500" />;
  }
  if (lowerCondition.includes('rain') || lowerCondition.includes('drizzle')) {
    return <CloudRain className="h-5 w-5 text-blue-400" />;
  }
  if (lowerCondition.includes('snow')) {
    return <Snowflake className="h-5 w-5 text-blue-200" />;
  }
  if (lowerCondition.includes('thunder') || lowerCondition.includes('storm')) {
    return <CloudLightning className="h-5 w-5 text-purple-400" />;
  }
  if (lowerCondition.includes('wind')) {
    return <Wind className="h-5 w-5 text-gray-400" />;
  }
  return <Cloud className="h-5 w-5 text-gray-400" />;
};

interface WeatherWidgetProps {
  className?: string;
  variant?: 'default' | 'compact';
}

export const WeatherWidget: React.FC<WeatherWidgetProps> = ({ className = '', variant = 'default' }) => {
  const { template } = useTemplate();
  const [weather, setWeather] = useState<WeatherData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const fetchWeather = async () => {
      try {
        setLoading(true);

        // 1. Try cached weather from the database first
        const { data: cached } = await pmsFrom('weather_cache')
          .select('temperature_f, weather_description, fetched_at')
          .limit(1)
          .maybeSingle();

        if (cached && !cancelled) {
          const age = Date.now() - new Date(cached.fetched_at).getTime();
          // Use cache if less than 30 minutes old
          if (age < 30 * 60 * 1000) {
            setWeather({
              temp: `${Math.round(cached.temperature_f)}°F`,
              condition: cached.weather_description || 'Unknown',
              icon: cached.weather_description || 'cloudy',
            });
            setLoading(false);
            return;
          }
        }

        // 2. Call edge function to refresh weather (avoids CORS)
        let data: any = null;
        let fetchErr: any = null;
        try {
          const result = await invokeFunction('fetch-weather');
          data = result.data;
        } catch (e) {
          fetchErr = e;
        }

        if (!fetchErr && data?.weather && !cancelled) {
          setWeather({
            temp: `${Math.round(data.weather.temperature_f)}°F`,
            condition: data.weather.description || 'Unknown',
            icon: data.weather.description || 'cloudy',
          });
        } else if (!cancelled) {
          // Use cached data even if stale, or fallback
          if (cached) {
            setWeather({
              temp: `${Math.round(cached.temperature_f)}°F`,
              condition: cached.weather_description || 'Unknown',
              icon: cached.weather_description || 'cloudy',
            });
          } else {
            setWeather({ temp: '72°F', condition: 'Partly Cloudy', icon: 'cloudy' });
          }
        }
      } catch {
        if (!cancelled) {
          setWeather({ temp: '72°F', condition: 'Partly Cloudy', icon: 'cloudy' });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchWeather();
    const interval = setInterval(fetchWeather, 30 * 60 * 1000);
    return () => { cancelled = true; clearInterval(interval); };
  }, [template.contact.city]);

  if (loading) {
    return (
      <div className={`flex items-center gap-2 text-sm ${variant === 'compact' ? '' : 'text-muted-foreground'} ${className}`}>
        <Loader2 className="h-4 w-4 animate-spin" />
        {variant !== 'compact' && <span>Loading...</span>}
      </div>
    );
  }

  if (!weather) return null;

  if (variant === 'compact') {
    return (
      <div className={`flex items-center gap-2 text-sm ${className}`}>
        {getWeatherIcon(weather.condition)}
        <span className="font-medium">{weather.temp}</span>
        <span className="opacity-80 hidden md:inline">{template.contact.city}</span>
      </div>
    );
  }

  return (
    <div className={`flex items-center gap-2 text-sm ${className}`}>
      {getWeatherIcon(weather.condition)}
      <span className="font-medium">{weather.temp}</span>
      <span className="text-muted-foreground hidden sm:inline">in {template.contact.city}</span>
    </div>
  );
};
